import { UserRole, MemberRole } from '../types/database.types'
import { supabase } from '../../shared/api/supabase'

// Role hierarchy (higher = more permissions)
export const ROLE_HIERARCHY = {
  'super_admin': 4,
  'admin': 3,
  'manager': 2,
  'member': 1
} as const

// Check if a role has at least the required level
export function hasRole(userRole: UserRole, requiredRole: UserRole): boolean {
  return ROLE_HIERARCHY[userRole] >= ROLE_HIERARCHY[requiredRole]
}

// Check if user can perform admin actions
export function canAdmin(userRole: UserRole): boolean {
  return hasRole(userRole, 'admin')
}

// Check if user can perform manager actions
export function canManage(userRole: UserRole): boolean {
  return hasRole(userRole, 'manager')
}

// Check if user is super admin
export function isSuperAdmin(userRole: UserRole): boolean {
  return userRole === 'super_admin'
}

// Get role display name
export function getRoleDisplayName(role: UserRole | MemberRole): string {
  switch (role) {
    case 'super_admin':
      return 'Super Admin'
    case 'admin':
      return 'Admin'
    case 'manager':
      return 'Manager'
    case 'member':
      return 'Member'
    default:
      return 'Unknown'
  }
}

// Get role color for UI
export function getRoleColor(role: UserRole | MemberRole): string {
  switch (role) {
    case 'super_admin':
      return 'bg-red-100 text-red-800'
    case 'admin':
      return 'bg-purple-100 text-purple-800'
    case 'manager':
      return 'bg-blue-100 text-blue-800'
    case 'member':
      return 'bg-gray-100 text-gray-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// Check if user can manage other users
export function canManageUsers(userRole: UserRole): boolean {
  return hasRole(userRole, 'admin')
}

// Check if user can create/delete groups
export function canManageGroups(userRole: UserRole): boolean {
  return hasRole(userRole, 'admin')
}

// Check if user can create curriculum templates (Manager, Admin, Super Admin)
export function canCreateCurriculum(userRole: UserRole): boolean {
  return hasRole(userRole, 'manager')
}

// Check if user can manage tasks
export function canManageTasks(userRole: UserRole): boolean {
  return hasRole(userRole, 'manager')
}

// Check if user can view analytics (Admin, Super Admin)
export function canViewAnalytics(userRole: UserRole): boolean {
  return hasRole(userRole, 'admin')
}

// Check if user can manage announcements (Manager, Admin, Super Admin)
export function canManageAnnouncements(userRole: UserRole): boolean {
  return hasRole(userRole, 'manager')
}

// Check if user can manage group settings (Manager, Admin, Super Admin)
export function canManageGroupSettings(userRole: UserRole): boolean {
  return hasRole(userRole, 'manager')
}

// Check if user can assign tasks (Manager, Admin, Super Admin)
export function canAssignTasks(userRole: UserRole): boolean {
  return hasRole(userRole, 'manager')
}

// Check if user can view all group data (Manager, Admin, Super Admin)
export function canViewGroupData(userRole: UserRole): boolean {
  return hasRole(userRole, 'manager')
}

// Check if user can create groups (only Admin and Super Admin)
export function canCreateGroups(userRole: UserRole): boolean {
  return hasRole(userRole, 'admin')
}

// Check if user can delete groups (only Admin and Super Admin)
export function canDeleteGroups(userRole: UserRole): boolean {
  return hasRole(userRole, 'admin')
}

// Check if user can manage group members (Admin and Manager)
export function canManageGroupMembers(userRole: UserRole): boolean {
  return hasRole(userRole, 'manager')
}

// Check if user can promote members to admin (only Admin and Super Admin)
export function canPromoteToAdmin(userRole: UserRole): boolean {
  return hasRole(userRole, 'admin')
}

// Get available roles for assignment (based on current user's role)
export function getAssignableRoles(currentUserRole: UserRole): UserRole[] {
  switch (currentUserRole) {
    case 'super_admin':
      return ['admin', 'manager', 'member']
    case 'admin':
      return ['manager', 'member']
    case 'manager':
      return ['member']
    case 'member':
      return []
    default:
      return []
  }
}

// Check if user can assign a specific role
export function canAssignRole(currentUserRole: UserRole, targetRole: UserRole): boolean {
  return getAssignableRoles(currentUserRole).includes(targetRole)
}

// Get user's role in a specific group
export async function getUserRoleInGroup(userId: string, groupId: string): Promise<string | null> {
  try {
    const { data, error } = await supabase
      .from('group_members')
      .select('role')
      .eq('user_id', userId)
      .eq('group_id', groupId)
      .single()

    if (error) {
      console.error('Error fetching user role in group:', error)
      return null
    }

    return data?.role || null
  } catch (error) {
    console.error('Error getting user role in group:', error)
    return null
  }
} 